import React, { useState, useEffect, useCallback } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import Typography from '@material-ui/core/Typography'
import BootstrapTable from 'react-bootstrap-table-next'
import Loader from 'components/general/Loader'
import AdminAPI from 'config/api/AdminAPI'
import { onError } from 'config/lib/errorLib'
import componentStyles from 'assets/theme/views/admin/react-bs-table.js'
import moment from 'moment-timezone'

const useStyles = makeStyles(componentStyles)

const TYPE_LABELS = {
    'topup': 'Topup',
    'refund': 'Refund (return)',
    'charge': 'Charge (allocation)',
    'manual-edit': 'Manual edit',
    'missed-charge': 'MISSED CHARGE'
}

function typeLabel(type) {
    return TYPE_LABELS[type] || type
}

function BalanceLedgerFeed(props) {
    const classes = { ...useStyles() }
    const api = new AdminAPI()

    const companyFilter = props.companyFilter || 'all'
    const companies = props.companies || []

    const [items, setItems] = useState([])
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)

    const companyName = useCallback((companyId) => {
        const company = companies.find((c) => c.companyId === companyId)
        return company ? company.name : companyId
    }, [companies])

    const loadData = useCallback(async () => {
        setLoading(true)
        try {
            if (companyFilter === 'all') {
                const res = await api.getBalanceLedgerFeed(null, 'pensions')
                setItems(res.items || [])
                setLastEvaluatedKey(res.lastEvaluatedKey || null)
            } else {
                const res = await api.getBalanceLedger(companyFilter, 'pensions')
                setItems(res || [])
                setLastEvaluatedKey(null)
            }
        } catch (e) {
            onError(e)
        }
        setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyFilter])

    useEffect(() => {
        loadData()
    }, [loadData, props.refreshSignal])

    async function loadMore() {
        if (!lastEvaluatedKey) return
        setLoadingMore(true)
        try {
            const res = await api.getBalanceLedgerFeed(lastEvaluatedKey, 'pensions')
            setItems((prev) => [...prev, ...(res.items || [])])
            setLastEvaluatedKey(res.lastEvaluatedKey || null)
        } catch (e) {
            onError(e)
        }
        setLoadingMore(false)
    }

    function formatReference(ref) {
        if (!ref) return '-'
        return Object.entries(ref).map(([k, v]) => `${k}: ${v}`).join(', ')
    }

    const columns = [
        {
            dataField: 'createdAt',
            text: 'Date',
            formatter: (cell) => moment(cell).format('DD/MM/YY HH:mm')
        },
        {
            dataField: 'companyId',
            text: 'Company',
            formatter: (cell) => `${companyName(cell)} (${(cell || '').toUpperCase()})`
        },
        {
            dataField: 'type',
            text: 'Type',
            formatter: (cell) => {
                const alert = cell === 'missed-charge'
                return <Box style={alert ? { color: '#f5365c', fontWeight: 600 } : {}}>{typeLabel(cell)}</Box>
            }
        },
        {
            dataField: 'amount',
            text: 'Amount',
            formatter: (cell) => {
                const amount = parseFloat(cell || 0)
                const color = amount > 0 ? '#2dce89' : (amount < 0 ? '#f5365c' : undefined)
                const sign = amount > 0 ? '+' : ''
                return <Box style={color ? { color } : {}}>{sign}€{amount.toFixed(2)}</Box>
            }
        },
        {
            dataField: 'balanceAfter',
            text: 'Balance After',
            formatter: (cell) => (cell != null ? `€${parseFloat(cell).toFixed(2)}` : '-')
        },
        {
            dataField: 'reference',
            text: 'Reference',
            formatter: (cell) => formatReference(cell)
        },
        {
            dataField: 'actor',
            text: 'Actor',
            formatter: (cell) => cell || '-'
        },
        {
            dataField: 'note',
            text: 'Reason / Note',
            formatter: (cell) => cell || '-'
        }
    ]

    const title = companyFilter === 'all'
        ? 'Balance Ledger \u2014 All Companies'
        : `Balance Ledger \u2014 ${companyName(companyFilter)} (${companyFilter.toUpperCase()})`

    return (
        <Card>
            <CardHeader
                subheader={
                    <Box display='flex' style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box
                            component={Typography}
                            variant='h2'
                            marginBottom='0!important'
                        >
                            <Box component='span'>{title}</Box>
                        </Box>
                        <Button
                            color='primary'
                            variant='outlined'
                            size='small'
                            onClick={() => loadData()}
                        >
                            Refresh
                        </Button>
                    </Box>
                }
                classes={{ root: classes.cardHeaderRoot }}
            ></CardHeader>

            {loading ? (
                <Box display='flex' justifyContent='center' alignItems='center' style={{ height: '20vh' }}>
                    <Loader />
                </Box>
            ) : (
                <>
                    <div className='table-container'>
                        <BootstrapTable
                            keyField='createdAt'
                            data={items}
                            columns={columns}
                            noDataIndication={<Box>No ledger entries</Box>}
                        />
                    </div>
                    {companyFilter === 'all' && lastEvaluatedKey &&
                        <Box display='flex' justifyContent='center' style={{ padding: '1rem' }}>
                            <Button
                                color='primary'
                                variant='contained'
                                size='small'
                                onClick={() => loadMore()}
                                disabled={loadingMore}
                            >
                                {loadingMore ? 'Loading...' : 'Load More'}
                            </Button>
                        </Box>
                    }
                </>
            )}
        </Card>
    )
}

export default BalanceLedgerFeed
